import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import EmailVerification from "./email-verification/email-verification";
import Logo from "src/components/molecules/logo/Logo";
import { ILoggedInUser, useAuth } from "./auth-context";

import { refreshTokens } from ".";
import login from "./login";
import { PermissionBagService } from "./permissionBagService";
import { AccountViewModel } from "../account-selection/account-selection-view-model";
import { TokenHelper } from "./token-helper";

const Verify: React.FC = () => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const username = location.state?.businessEmail;
	const accountViewModel = useRef<AccountViewModel | null>(null);
	const tokenHelper = useRef<TokenHelper | null>(null);

	useEffect(() => {
		if (user && user.accessToken) {
			tokenHelper.current = new TokenHelper();
			accountViewModel.current = new AccountViewModel(user.accessToken);
		}
	}, []);

	const handleEmailVerification = async (isSuccess: boolean) => {
		if (isSuccess) {
			if (user && user.accountId) {
				try {
					if (accountViewModel.current) {
						const result =
							await accountViewModel.current.setSessionAsync(
								user.accountId
							);
						if (result) {
							refreshTokens(async (err, tokenResult) => {
								if (tokenResult) {
									const decodedIdToken =
										await tokenHelper.current?.decodeToken(
											tokenResult.idToken
										);

									const decodedAccessToken =
										await tokenHelper.current?.decodeToken(
											tokenResult.accessToken
										);

									if (decodedAccessToken) {
										const permissionService =
											new PermissionBagService(
												tokenResult.accessToken
											);
										const permissions =
											await permissionService.getPermissionBag(
												decodedAccessToken?.role ?? ""
											);

										const loggedInUser: ILoggedInUser = {
											id: decodedAccessToken.sub,
											givenName:
												decodedAccessToken.given_name,
											familyName:
												decodedAccessToken.family_name,
											username: decodedAccessToken.email,
											phoneNumber: `${decodedIdToken?.phone_number ?? 0}`,
											ex_user_id:
												decodedAccessToken.username,
											accessToken:
												tokenResult.accessToken,
											accountId:
												decodedAccessToken.account_id ??
												"",
											email:
												decodedAccessToken.email ?? "",
											role: decodedAccessToken.role,
											exp: decodedAccessToken.exp,
											permissionBag: permissions
												? permissions
												: { permissions: [] },
											has_multiple_accounts:
												decodedAccessToken.has_multiple_accounts,
										};

										login(loggedInUser); // Directly log in the user

										navigate("/");
									} else {
										navigate("/error");
									}
								} else {
									console.error(
										"Failed to refresh tokens:",
										err
									);
								}
							});
						}
					}
				} catch (error) {
					console.error("Error in account selection:", error);
				}

				navigate("/");
			} else {
				navigate("/select-account");
			}
		} else {
			console.log("Email verification failed");
		}
	};

	return (
		<>
			<div className="md:bg-app-gradient flex flex-col items-center justify-start md:justify-center h-screen w-screen md:py-8">
				<div className="bg-white text-gray-900 flex flex-col gap-6 items-center justify-start md:justify-center h-full md:h-fit w-full md:w-fit p-12 md:rounded-2xl md:shadow-[0_30px_50px_rgba(0,0,0,.5)]">
					<Logo></Logo>
					<span className="font-Poppins text-center tracking-wider bg-gradient-to-bl from-purple-600 to-red-800 text-4xl md:text-6xl font-bold lg:text-4xl bg-clip-text text-transparent drop-shadow-2xl">
						Verify your email address
					</span>
					<EmailVerification
						emailAddress={username}
						onVerification={handleEmailVerification}
					></EmailVerification>
				</div>
			</div>
		</>
	);
};

export default Verify;
